import type { Project } from "@/lib/types";
import { Link } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "./StarRating";
import { GitFork, MessageSquare, Box, Layers, Thermometer } from "lucide-react";

interface ProjectCardProps {
	project: Project;
}

const materialColors: Record<string, string> = {
	PLA: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
	ABS: "bg-red-500/15 text-red-400 border-red-500/20",
	PETG: "bg-blue-500/15 text-blue-400 border-blue-500/20",
	TPU: "bg-purple-500/15 text-purple-400 border-purple-500/20",
	Nylon: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
	Resin: "bg-pink-500/15 text-pink-400 border-pink-500/20",
	Other: "bg-gray-500/15 text-gray-400 border-gray-500/20",
};

export function ProjectCard({ project }: ProjectCardProps) {
	return (
		<Link to={`/project/${project.id}`} className="block group">
			<Card className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-all duration-300 hover:border-border hover:shadow-lg hover:shadow-orange-500/5 hover:-translate-y-0.5">
				{/* Thumbnail Area */}
				<div className="relative aspect-[4/3] bg-gradient-to-br from-muted/80 to-muted flex items-center justify-center overflow-hidden">
					<div className="flex flex-col items-center gap-2 text-muted-foreground/40">
						<Box className="h-12 w-12" strokeWidth={1} />
						<span className="text-xs font-medium uppercase tracking-wider">STL Preview</span>
					</div>
					{project.forkedFrom && (
						<div className="absolute top-2 left-2">
							<Badge variant="secondary" className="text-[10px] gap-1 bg-background/80 backdrop-blur-sm">
								<GitFork className="h-3 w-3" /> Fork
							</Badge>
						</div>
					)}
					<Badge
						className={`absolute top-2 right-2 text-[10px] font-semibold border ${materialColors[project.printSettings.material] || materialColors.Other}`}
					>
						{project.printSettings.material}
					</Badge>
				</div>

				<CardContent className="p-4 space-y-3">
					<div>
						<h3 className="font-semibold text-sm leading-tight group-hover:text-orange-400 transition-colors line-clamp-1">
							{project.title}
						</h3>
						<p className="text-xs text-muted-foreground mt-1 line-clamp-2">{project.description}</p>
					</div>

					{/* Print Settings Quick View */}
					<div className="flex items-center gap-3 text-[11px] text-muted-foreground">
						<span className="flex items-center gap-1">
							<Layers className="h-3 w-3" />
							{project.printSettings.layerHeight}mm
						</span>
						<span className="flex items-center gap-1">
							<Thermometer className="h-3 w-3" />
							{project.printSettings.nozzleTemp}°C
						</span>
						<span>{project.printSettings.infillPercent}% infill</span>
					</div>

					{/* Footer */}
					<div className="flex items-center justify-between pt-1 border-t border-border/50">
						<div className="flex items-center gap-1.5">
							<div className="h-5 w-5 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-[10px] font-bold text-white">
								{project.creator.displayName[0]}
							</div>
							<span className="text-xs text-muted-foreground">{project.creator.displayName}</span>
						</div>
						<div className="flex items-center gap-3 text-xs text-muted-foreground">
							{project.ratingCount > 0 && (
								<span className="flex items-center gap-1">
									<StarRating rating={project.averageRating} size="sm" />
									<span className="text-[11px]">({project.ratingCount})</span>
								</span>
							)}
							{project.forkCount > 0 && (
								<span className="flex items-center gap-1">
									<GitFork className="h-3 w-3" />
									{project.forkCount}
								</span>
							)}
							{project.printResults.length > 0 && (
								<span className="flex items-center gap-1">
									<MessageSquare className="h-3 w-3" />
									{project.printResults.length}
								</span>
							)}
						</div>
					</div>
				</CardContent>
			</Card>
		</Link>
	);
}
